/**
 * TikTok Pixel — client-side event firing for shop pages, CONSENT-GATED.
 *
 * Loads the TikTok Pixel JS and exposes a `useTikTokPixel()` composable
 * wrapper for emitting events. Only activates when the tenant has
 * `tiktokPixel.pixelId` configured + `enabled` (per the /api/tenant/current
 * response) — AND the visitor has granted the `marketing` cookie-consent
 * category. The pre-2026-08 version loaded the pixel unconditionally, which
 * on a German storefront meant a marketing tracker firing before opt-in
 * while the banner on the same page promised a choice; measured conversions
 * DROP with this fix (declined visitors are no longer counted) — that is the
 * correct number replacing an unlawful one, not a regression.
 *
 * Lifecycle mirrors meta-pixel.client.ts:
 *   - no consent  → no script injected, no cookies, calls dropped.
 *   - granted (now or later via the banner) → inject, ttq.page(), SPA page
 *     views on navigation.
 *   - revoked after load → ttq.disableCookie() + all further sending from us
 *     is suppressed.
 *
 * Server-side counterpart: server/utils/tiktokEvents.ts emits CompletePayment
 * from the Stripe webhook. Dedup pairs browser+server via the deterministic
 * shared event id `order:<orderId>`.
 */
const STAFF_HOST_PREFIXES = ['admin.', 'creator.', 'labels.', 'scouts.', 'superadmin.', 'lnk.']

export default defineNuxtPlugin(() => {
    if (typeof window === 'undefined') return

    const noop = {
        provide: {
            tiktokPixel: {
                track: (_event: string, _props?: Record<string, any>, _eventId?: string) => {},
                identify: (_user: { email?: string; phone?: string; externalId?: string }) => {},
                page: () => {},
                enabled: false,
            },
        },
    }

    const host = window.location.host
    if (STAFF_HOST_PREFIXES.some(p => host.startsWith(p))) return noop

    const tenantState = useState<any>('tenant')
    const pixelId = tenantState.value?.tiktokPixel?.pixelId
    if (!pixelId) return noop

    const { state: consent } = useCookieConsent()
    let loaded = false
    let suppressed = false

    // TikTok's standard snippet (copied from the Events Manager dashboard,
    // parameterised on pixel id) — injected only once consent is granted,
    // because loading events.js is itself what sets the _ttp cookie.
    function loadPixel() {
        if (loaded) return
        loaded = true
        const w = window as any
        w.TiktokAnalyticsObject = 'ttq'
        const ttq: any = (w.ttq = w.ttq || [])
        ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie']
        ttq.setAndDefer = function (t: any, e: string) {
            t[e] = function () {
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
            }
        }
        for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i])
        ttq.instance = function (t: string) {
            const e = ttq._i[t] || []
            for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n])
            return e
        }
        ttq.load = function (e: string, n?: any) {
            const r = 'https://analytics.tiktok.com/i18n/pixel/events.js'
            const o = (n && n.partner) || ''
            ttq._i = ttq._i || {}
            ttq._i[e] = []
            ttq._i[e]._u = r
            ttq._t = ttq._t || {}
            ttq._t[e] = +new Date()
            ttq._o = ttq._o || {}
            ttq._o[e] = n || {}
            const s = document.createElement('script')
            s.type = 'text/javascript'
            s.async = true
            s.src = r + '?sdkid=' + e + '&lib=' + o
            const x = document.getElementsByTagName('script')[0]
            x?.parentNode?.insertBefore(s, x)
        }
        ttq.load(pixelId)
        ttq.page()
    }

    // React to the banner in both directions. `immediate` covers a visitor
    // whose stored consent already grants marketing.
    watch(() => consent.value.marketing, (granted) => {
        if (granted) {
            suppressed = false
            if (loaded) { try { (window as any).ttq?.enableCookie() } catch {} }
            loadPixel()
        } else if (loaded) {
            suppressed = true
            try { (window as any).ttq?.disableCookie() } catch {}
        }
    }, { immediate: true })

    const canSend = () => loaded && !suppressed

    // Fire `page` on every navigation so SPA transitions register as views.
    const router = useRouter()
    router.afterEach(() => {
        if (!canSend()) return
        try { (window as any).ttq?.page() } catch {}
    })

    return {
        provide: {
            tiktokPixel: {
                /**
                 * Fire a TikTok pixel event. Standard event names —
                 * 'ViewContent', 'AddToCart', 'InitiateCheckout',
                 * 'CompletePayment' — get auto-attribution; custom
                 * names require manual setup in the Events Manager.
                 *
                 * Pair the eventId with the server-side event-id
                 * to dedupe between browser + server fires.
                 */
                track: (event: string, props?: Record<string, any>, eventId?: string) => {
                    if (!canSend()) return
                    try {
                        const opts: any = props || {}
                        if (eventId) opts.event_id = eventId
                        ;(window as any).ttq?.track(event, opts)
                    } catch (err) {
                        console.warn('[tiktok-pixel] track failed:', err)
                    }
                },
                identify: (user: { email?: string; phone?: string; externalId?: string }) => {
                    if (!canSend()) return
                    try {
                        ;(window as any).ttq?.identify({
                            email: user.email,
                            phone_number: user.phone,
                            external_id: user.externalId,
                        })
                    } catch (err) {
                        console.warn('[tiktok-pixel] identify failed:', err)
                    }
                },
                page: () => {
                    if (!canSend()) return
                    try { (window as any).ttq?.page() } catch {}
                },
                enabled: true,
            },
        },
    }
})
